import { useCallback, useEffect, useRef, useState } from "react"

import {
	CANCEL_FEE_LOTTERY_CAMPAIGN_NAME,
	CANCEL_FEE_LOTTERY_PRIZE_GIFT_NAME,
	CANCEL_FEE_LOTTERY_WINNER_SMS_DAYS,
	CANCEL_FEE_LOTTERY_WINNER_TEXT,
	CancelFeeLottery,
	cancelFeeLotteryPrizeText,
} from "./cancelFeeLottery"

import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import BrandMarquee from "@/public/components/features/rereservation/RereservationIncentiveBanner/BrandMarquee"
import LotteryRoulette from "@/public/components/features/rereservation/RereservationIncentiveBanner/LotteryRoulette"
import styles from "@/public/components/features/rereservation/RereservationIncentiveBanner/LotteryRoulette.module.scss"
import {
	hasSpunLottery,
	markLotteryAsSpun,
} from "@/public/components/features/rereservation/RereservationIncentiveBanner/lotteryStorage"
import { CancelFeeLotteryRouletteOpenTrigger } from "@/public/event"
import { toStringWithCommas } from "@/utils/strings/number"

type Props = {
	lottery: CancelFeeLottery
	cancelId: string
	customerName: string
	// 請求画面に出している支払い期日 (YYYY/MM/DD). 応募条件の期限として見せる.
	paymentDeadline: string
	// バナーを出した直後にルーレットのモーダルを自動で開き、タップなしで回し始めるか
	// (見せ方の A/B テストの auto_open 群. 「回す」を押す手間を省き、開いた時点でもう回っている).
	// 群は lottery.autoOpenRoulette だが、コンビニ/銀行振込の confirm から戻ってきた時など
	// 支払いの途中に被せたくない場面はページ側が false にするので、ページ側で決めて渡す.
	// この端末で回し終えている時は true でも開かない.
	autoOpen: boolean
	// バナーを画面に描画した (マウントした) 時に呼ぶ (fe_event 用. 見せ方 A/B の母数).
	onBannerShown: () => void
	// お客様に抽選の案内を見せ終えた (ルーレットを回し終えた) 時に呼ぶ.
	// サーバ (lottery_tickets.shown_time) への記録はページ側が行う (再予約側 LotteryBanner と同じ形).
	onShown: () => void
	// ルーレットのモーダルが開いた時に呼ぶ (fe_event 用). trigger は自動で開いたか、ボタンで開いたか.
	onRouletteOpen: (trigger: CancelFeeLotteryRouletteOpenTrigger) => void
	// 回さずにモーダルを閉じた時に呼ぶ (fe_event 用).
	onRouletteClosedWithoutSpinning: () => void
}

// 請求画面に置く、キャンセル料お支払い 10 万円ルーレットのバナー.
// 見た目と演出は再予約側 (LotteryBanner) と同じ部品を使い、応募条件だけ
// 「支払い期日までにお支払い」に差し替えている.
//
// 再予約側と違い、読み飛ばした人にスクロール連動でルーレットを自動で開くことはしない
// (請求画面の本題はお支払いなので、支払いフォームの操作中に被せないため).
// 代わりに A/B テストの auto_open 群だけ、バナーを出した直後 (支払いフォームを触る前) に 1 度自動で開き、
// 「回す」を押さなくてもそのまま回し始める (自動で開いた時だけ. バナーのボタンから開いた時はタップで回す).
const CancelFeeLotteryBanner = ({
	lottery,
	cancelId,
	customerName,
	paymentDeadline,
	autoOpen,
	onBannerShown,
	onShown,
	onRouletteOpen,
	onRouletteClosedWithoutSpinning,
}: Props) => {
	const [isSpun, setIsSpun] = useState(() =>
		hasSpunLottery("cancel_fee", cancelId)
	)
	const [isRouletteOpen, setIsRouletteOpen] = useState(false)
	// 今開いているモーダルが自動で開いたものか. 自動で開いた時はタップなしで回し始める.
	const [isAutoSpin, setIsAutoSpin] = useState(false)
	// ルーレットを回している間〜結果表示の間だけ、モーダルの外側 (背景) をさらに暗くする.
	const [isStageDark, setIsStageDark] = useState(false)
	// モーダルを閉じた時に「回さずに閉じた」かを判定するため、回し終えたかを ref でも持つ
	// (onOpenChange は Radix から呼ばれるので、state の更新を待たずに読める値が要る).
	const hasSpunRef = useRef(isSpun)

	const openRoulette = useCallback(
		(trigger: CancelFeeLotteryRouletteOpenTrigger) => {
			setIsAutoSpin(trigger === "auto")
			setIsRouletteOpen(true)
			onRouletteOpen(trigger)
		},
		[onRouletteOpen]
	)

	// バナーを描画した = お客様がルーレットを見た. 自動オープンより先に送る (母数 → 開いた、の順にする).
	useEffect(() => {
		onBannerShown()
		// マウント直後の 1 度だけ送る.
	}, [])

	// auto_open 群: バナーを出した直後に 1 度だけ自動で開き、そのまま回し始める. この端末で回し終えていれば開かない.
	// 回し終えるまでは再訪 (2 通目以降の SMS からの流入) のたびに開く. 回り終わる前に閉じただけなら
	// 次の訪問でまた開く (訪問ごとに 1 回目と同じ体験にする).
	useEffect(() => {
		if (!autoOpen || isSpun) {
			return
		}
		openRoulette("auto")
		// 自動で開くのはマウント直後の 1 度だけ. 回した後の state 変化で再び開かないよう依存は持たない.
	}, [])

	const handleSpinStart = useCallback(() => {
		setIsStageDark(true)
	}, [])

	const handleRouletteOpenChange = useCallback(
		(open: boolean) => {
			setIsRouletteOpen(open)
			if (!open) {
				setIsStageDark(false)
				if (!hasSpunRef.current) {
					onRouletteClosedWithoutSpinning()
				}
			}
		},
		[onRouletteClosedWithoutSpinning]
	)

	// 回す演出を出すかはこの端末 (localStorage) で回したかだけで決める (サーバの記録は測定用で見ない).
	// QA でお客様の画面を別の端末から開いたときにも、ルーレットを回して確認できるようにするため.
	const handleSpun = useCallback(() => {
		markLotteryAsSpun("cancel_fee", cancelId)
		hasSpunRef.current = true
		setIsSpun(true)
		onShown()
	}, [cancelId, onShown])

	const { multiplier, prizePrice, wheelMultipliers } = lottery
	const maxMultiplier = Math.max(...wheelMultipliers)

	let rouletteContentClassName = styles.rouletteContent
	if (isStageDark) {
		rouletteContentClassName = `${styles.rouletteContent} ${styles.rouletteContentDark}`
	}

	let boost: JSX.Element
	if (isSpun) {
		boost = (
			<div className={`${styles.bannerBoost} ${styles.bannerBoostApplied}`}>
				<div className={styles.bannerBoostLabel}>ルーレットの結果</div>
				<div className={styles.bannerApplied}>
					<span>当選確率</span>
					<span className={styles.bannerAppliedNumber}>{multiplier}</span>
					<span className={styles.bannerAppliedUnit}>倍</span>
					<span>が適用されています</span>
				</div>
			</div>
		)
	} else {
		boost = (
			<div className={styles.bannerBoost}>
				<div className={styles.bannerBoostHead}>
					<div className={styles.bannerBoostWheel} aria-hidden="true" />
					<div className={styles.bannerBoostText}>
						<span className={styles.bannerBoostBadge}>期間限定</span>
						<div className={styles.bannerBoostHeadline}>
							今なら<strong>当選確率UP</strong>
							<br />
							ルーレットが回せる！
						</div>
					</div>
				</div>
				<div className={styles.bannerBoostMax}>
					当選確率が最大
					<span className={styles.bannerBoostMaxNumber}>{maxMultiplier}</span>
					倍に
				</div>
				<button
					type="button"
					className={styles.bannerCta}
					onClick={() => openRoulette("click")}
				>
					ルーレットを回す
				</button>
			</div>
		)
	}

	return (
		<>
			<div className={styles.banner}>
				<div className={styles.bannerHeader}>
					<div className={styles.bannerHeaderTitle}>{customerName}様</div>
					<span className={styles.bannerHeaderBadge}>期間限定</span>
				</div>

				<div className={styles.bannerMain}>
					<div className={styles.bannerCampaignName}>
						{CANCEL_FEE_LOTTERY_CAMPAIGN_NAME}
					</div>
					<div className={styles.bannerSubtitle}>
						抽選で
						<strong className={styles.bannerSubtitleCount}>
							{CANCEL_FEE_LOTTERY_WINNER_TEXT}
						</strong>
						に当たる
					</div>
					<div className={styles.bannerAmountRow}>
						<div className={styles.bannerAmount}>
							{toStringWithCommas(prizePrice)}
						</div>
						<div className={styles.bannerAmountUnit}>
							<span className={styles.bannerAmountUnitYen}>円分</span>
							<span>のポイント</span>
						</div>
					</div>
					<div className={styles.bannerNote}>
						{CANCEL_FEE_LOTTERY_PRIZE_GIFT_NAME}で好きなポイントに交換できます
					</div>
				</div>

				<BrandMarquee />

				{/* 応募条件は賞品 (金額と交換先のロゴ) のすぐ下で大きく見せる. ルーレットを回しただけで応募できたと
				    誤解されないよう「期日までのお支払い」を一番強く出す. */}
				<div className={styles.bannerCondition}>
					<div className={styles.bannerConditionLabel}>抽選の応募条件</div>
					<div className={styles.bannerConditionLead}>
						お支払い期日（{paymentDeadline}）までに
					</div>
					<div className={styles.bannerConditionMain}>
						<strong className={styles.bannerConditionVisit}>お支払い</strong>
						<span>で応募完了</span>
					</div>
					<div className={styles.bannerConditionNote}>
						※ 抽選に参加してもご請求金額は変わりません
					</div>
				</div>

				{boost}

				{/* 抽選番号は cancelId をそのまま見せる (問い合わせ時の照合用. 再予約側と同じ). */}
				<ul className={styles.bannerFinePrint}>
					<li>
						抽選番号:{" "}
						<span className={styles.bannerFinePrintNumber}>{cancelId}</span>
					</li>
					<li>
						当選されたお客様へは、お支払い後
						{CANCEL_FEE_LOTTERY_WINNER_SMS_DAYS}
						日以内にSMSにてご連絡いたします。
					</li>
					<li>詳細はキャンペーン規約（ページ下部）をご確認ください。</li>
				</ul>
			</div>

			<ModalDialog
				isOpen={isRouletteOpen}
				onOpenChange={handleRouletteOpenChange}
				contentClassName={rouletteContentClassName}
			>
				<LotteryRoulette
					multiplier={multiplier}
					wheelMultipliers={wheelMultipliers}
					winnerText={CANCEL_FEE_LOTTERY_WINNER_TEXT}
					resultNote={
						<>
							お支払い期日（{paymentDeadline}）までに<strong>お支払い</strong>
							いただくと、{multiplier} 倍の当選確率で抽選に応募されます。
						</>
					}
					resultButtonLabel="お支払いへ進む"
					prizeText={cancelFeeLotteryPrizeText(prizePrice)}
					customerName={customerName}
					autoSpin={isAutoSpin}
					onSpinStart={handleSpinStart}
					onSpun={handleSpun}
					onClose={() => handleRouletteOpenChange(false)}
				/>
			</ModalDialog>
		</>
	)
}

export default CancelFeeLotteryBanner
